/**
 * Month/period-matrix resolver.
 *
 * Recognizes wide month/half-month matrix files (Citisign, Sequence,
 * ZAKmedia, Edpol, Parejo, Bridge42, Koneser family): one column per
 * calendar month or half-month, each header parseable as a Polish month
 * name or an `MM/YYYY`-style date, with cell values holding a status token
 * per unit per period.
 *
 * Traceability: availability-profile-resolution / Requirement: Month/period-matrix
 * resolver.
 */
import type {
  AvailabilityProfile,
  AvailabilityTimeEncoding,
} from '../../schemas/availability-profile.js';
import type { AvailabilityProfileResolver, AvailabilityProfileResolveResult } from './types.js';
import { normalizeHeaderForMatch, pad } from './header-match.js';
import {
  CANONICAL_BUSY_TOKENS,
  CANONICAL_FREE_TOKENS,
  computeHalfMonthRange,
  normalizeToken,
} from './status-normalization.js';
import { buildAvailabilityProfileSkeleton } from './profile-skeleton.js';
import { detectColumns } from './generic-resolver.js';

const MIN_DATE_LIKE_COLUMNS = 3;
const HIGH_CONFIDENCE_COLUMN_COUNT = 6;

const POLISH_MONTHS: Record<string, number> = {
  styczeń: 1,
  luty: 2,
  marzec: 3,
  kwiecień: 4,
  maj: 5,
  czerwiec: 6,
  lipiec: 7,
  sierpień: 8,
  wrzesień: 9,
  październik: 10,
  listopad: 11,
  grudzień: 12,
};

/** Matches a first-half token: `1-15`/`01-15`, or the legacy roman `i`. */
const HALF_1_TOKEN_RE = /^0?1-15$/;
/** Matches a second-half token independently of the month's actual
 * end-of-month day: `16-28`/`16-29`/`16-30`/`16-31`, or the legacy roman
 * `ii`. The trailing day is supplier-entry noise — `computeHalfMonthRange`
 * computes the true calendar-aware end date regardless of what the header
 * text says. */
const HALF_2_TOKEN_RE = /^16-(28|29|30|31)$/;
const YEAR_4_TOKEN_RE = /^(19|20)\d{2}$/;
const YEAR_2_TOKEN_RE = /^\d{2}$/;

export interface DateLikeColumn {
  header: string;
  year: number;
  month: number;
  half: 1 | 2 | null;
  /** `false` for a bare Polish month name/half-suffix header — its `year` is
   * only a placeholder (the current year) and MUST be re-resolved by
   * {@link resolveColumnYears} before use. `true` for an explicit `MM/YYYY`
   * header, or a combined-header inline/reversed year token (`grudzień 26`,
   * `2027 styczeń`), whose stated year is authoritative and never rolled
   * forward. */
  hasExplicitYear: boolean;
}

/** Classifies one non-month token as a half-month signal, a year signal, or
 * neither (ignored) — extracted from {@link parseDateLikeHeader} to keep
 * that function's cyclomatic complexity within the lint limit. */
function classifyToken(
  token: string,
  current: { half: 1 | 2 | null; explicitYear: number | null },
): void {
  if (token === 'i' || HALF_1_TOKEN_RE.test(token)) {
    current.half = 1;
  } else if (token === 'ii' || HALF_2_TOKEN_RE.test(token)) {
    current.half = 2;
  } else if (YEAR_4_TOKEN_RE.test(token)) {
    current.explicitYear = Number(token);
  } else if (current.explicitYear === null && YEAR_2_TOKEN_RE.test(token)) {
    current.explicitYear = 2000 + Number(token);
  }
}

/**
 * Parses a (possibly two-row-combined, see `compound-header.ts`) header into
 * its month/half/year signal via whitespace tokenization: exactly one token
 * must match a Polish month name (any position); other tokens are scanned
 * for a half-month signal (`1-15`/`01-15`/`i` for the first half,
 * `16-28`..`16-31`/`ii` for the second) and a year signal (a 4-digit token,
 * or a bare 2-digit token interpreted as `2000 + token`). Any other token is
 * ignored rather than rejecting the header, matching the prior
 * suffix-array approach's leniency. Returns `null` when no month token is
 * found at all — not a month-matrix-shaped header.
 */
export function parseDateLikeHeader(header: string, referenceYear: number): DateLikeColumn | null {
  const normalized = normalizeHeaderForMatch(header);

  const numericMatch = normalized.match(/^(\d{1,2})[./-](\d{4})$/);
  if (numericMatch) {
    const month = Number(numericMatch[1]);
    const year = Number(numericMatch[2]);
    if (month >= 1 && month <= 12) {
      return { header, year, month, half: null, hasExplicitYear: true };
    }
    return null;
  }

  const tokens = normalized.split(' ');
  const monthEntry = Object.entries(POLISH_MONTHS).find(([name]) => tokens.includes(name));
  if (!monthEntry) return null;
  const [, month] = monthEntry;

  const signals: { half: 1 | 2 | null; explicitYear: number | null } = {
    half: null,
    explicitYear: null,
  };
  for (const token of tokens) classifyToken(token, signals);

  return signals.explicitYear === null
    ? { header, year: referenceYear, month, half: signals.half, hasExplicitYear: false }
    : { header, year: signals.explicitYear, month, half: signals.half, hasExplicitYear: true };
}

/**
 * Computes the ISO date range for a full calendar month. Exported (like its
 * half-month counterpart `computeHalfMonthRange`) so the availability-import
 * web feature's period-columns table can recompute a row's `periodStart`/
 * `periodEnd` client-side when the operator overrides that row's resolved
 * year, without duplicating this date math.
 */
export function computeMonthRange(year: number, month: number): { start: string; end: string } {
  const lastDay = new Date(year, month, 0).getDate();
  return { start: `${year}-${pad(month)}-01`, end: `${year}-${pad(month)}-${pad(lastDay)}` };
}

/**
 * Resolves the year to use for a bare month-only header (no year stated in
 * the header itself) with NO explicit-year anchor anywhere in the same
 * `resolve()` call: the first (soonest) occurrence of that month whose
 * period has not yet fully elapsed relative to `todayIso`. Computes the
 * candidate range using `referenceYear` (today's calendar year) and rolls
 * forward by exactly one year only when that candidate range's END date is
 * already before today — a month whose range ends today or later keeps the
 * current year. Applied ONLY to the FIRST date-like column in a file with no
 * explicit-year anchor anywhere (CU-12472jzd1ea, product decision: a file
 * with no year is one continuous season anchored by its first column) —
 * every later column then derives its year sequentially from the one before
 * it in file order via {@link resolveColumnYears}'s shared forward/backward
 * walk, never re-evaluated against today independently (that per-column
 * independence used to split one continuous season across two years, e.g.
 * a Jun-May run partly rolling forward and partly rolling back depending on
 * how far each individual month's own instance was from today).
 */
function resolveColumnYearRelativeToToday(
  referenceYear: number,
  month: number,
  half: 1 | 2 | null,
  todayIso: string,
): number {
  const candidate = half
    ? computeHalfMonthRange(referenceYear, month, half)
    : computeMonthRange(referenceYear, month);
  return candidate.end < todayIso ? referenceYear + 1 : referenceYear;
}

/**
 * Resolves every date-like column's year together, anchored by ONE seed
 * column, then walked forward/backward across the rest in FILE order —
 * incrementing the running year on each chronological month-number wrap,
 * decrementing on the mirrored backward walk (see design.md Decision 4).
 * The seed is: the FIRST explicit-year column, if the file has one (a
 * `MM/YYYY` header, or a combined-header inline/reversed year token — see
 * `parseDateLikeHeader`) — resyncing to any later column's own stated year
 * as the walk passes it; otherwise (CU-12472jzd1ea) the file's FIRST
 * date-like column, seeded via the per-column today-relative rule
 * ({@link resolveColumnYearRelativeToToday}) since no column in the file
 * states a year at all. Either way, every non-seed column's year comes
 * SOLELY from its neighbor in file order, never independently — a file
 * with no explicit year is one continuous season, and re-evaluating each
 * column against today independently could split that season across years.
 */
function resolveColumnYears(
  columns: DateLikeColumn[],
  referenceYear: number,
  todayIso: string,
): number[] {
  const anchorIndexes = columns
    .map((column, index) => (column.hasExplicitYear ? index : -1))
    .filter((index) => index !== -1);

  // A file with zero explicit-year columns is one continuous season anchored
  // by its FIRST column (CU-12472jzd1ea): seed that column via the same
  // today-relative rule as before, then let every later column fall out of
  // the forward-walk loop below exactly like an explicit anchor would —
  // never re-evaluated against today independently, which is what used to
  // split a continuous season across two years.
  const years = new Array<number>(columns.length);
  const firstAnchor = anchorIndexes.length > 0 ? anchorIndexes[0]! : 0;
  years[firstAnchor] =
    anchorIndexes.length > 0
      ? columns[firstAnchor]!.year
      : resolveColumnYearRelativeToToday(
          referenceYear,
          columns[0]!.month,
          columns[0]!.half,
          todayIso,
        );

  for (let i = firstAnchor + 1; i < columns.length; i++) {
    years[i] = columns[i]!.hasExplicitYear
      ? columns[i]!.year
      : years[i - 1]! + (columns[i]!.month < columns[i - 1]!.month ? 1 : 0);
  }
  for (let i = firstAnchor - 1; i >= 0; i--) {
    years[i] = columns[i]!.hasExplicitYear
      ? columns[i]!.year
      : years[i + 1]! - (columns[i]!.month > columns[i + 1]!.month ? 1 : 0);
  }

  return years;
}

function inferPolarity(dateLikeColumns: DateLikeColumn[], sampleRows: Record<string, string>[]) {
  let busyCount = 0;
  let nonBlankCount = 0;

  for (const row of sampleRows) {
    for (const column of dateLikeColumns) {
      const raw = row[column.header];
      if (raw === undefined) continue;
      const normalized = normalizeToken(raw);
      if (normalized === '') continue;

      nonBlankCount += 1;
      const isCanonicalFree = (CANONICAL_FREE_TOKENS as readonly string[]).includes(normalized);
      const isCanonicalBusy = (CANONICAL_BUSY_TOKENS as readonly string[]).includes(normalized);
      if (isCanonicalBusy || (!isCanonicalFree && normalized !== '')) {
        busyCount += 1;
      }
    }
  }

  if (nonBlankCount === 0) return 'list_of_free' as const;
  return busyCount / nonBlankCount > 0.5 ? ('list_of_busy' as const) : ('list_of_free' as const);
}

export const monthMatrixResolver: AvailabilityProfileResolver = {
  id: 'month-matrix',
  resolve(headers, sampleRows): AvailabilityProfileResolveResult | null {
    const today = new Date();
    const referenceYear = today.getFullYear();
    const todayIso = `${referenceYear}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const dateLikeColumns = headers
      .map((header) => parseDateLikeHeader(header, referenceYear))
      .filter((column): column is DateLikeColumn => column !== null);

    if (dateLikeColumns.length < MIN_DATE_LIKE_COLUMNS) return null;

    const isHalfMonth = dateLikeColumns.some((column) => column.half !== null);
    const timeEncoding: AvailabilityTimeEncoding = isHalfMonth ? 'half_month' : 'month';

    const columnYears = resolveColumnYears(dateLikeColumns, referenceYear, todayIso);
    const periodColumns = dateLikeColumns.map((column, index) => {
      const year = columnYears[index]!;
      const range = column.half
        ? computeHalfMonthRange(year, column.month, column.half)
        : computeMonthRange(year, column.month);
      return { column: column.header, periodStart: range.start, periodEnd: range.end };
    });

    const polarity = inferPolarity(dateLikeColumns, sampleRows);
    const confidence = dateLikeColumns.length >= HIGH_CONFIDENCE_COLUMN_COUNT ? 0.85 : 0.75;

    // Detect the NON-date columns (supplier number, GPS, address, city) so
    // row->unit matching can fire without the operator manually mapping the
    // number column — previously idColumns/addressColumns stayed at the
    // skeleton's nulls and every row of a month-matrix file was unmatched
    // (Bridge42_bb / Outdoor3miasto_pomorska, CU-86canaqmw). Only headers
    // outside the date-like set are considered, so a month column can never
    // be claimed as an id/address column.
    const dateLikeHeaders = new Set(dateLikeColumns.map((column) => column.header));
    const nonDateHeaders = headers.filter((header) => !dateLikeHeaders.has(header));
    const columns = detectColumns(nonDateHeaders);

    const profile: AvailabilityProfile = {
      ...buildAvailabilityProfileSkeleton(),
      idColumns: {
        grwIdColumn: columns.grwIdHeader ?? null,
        supplierNumberColumn: columns.supplierNumberHeader ?? null,
      },
      gpsColumns: { latColumn: columns.latHeader ?? null, lngColumn: columns.lngHeader ?? null },
      addressColumns: {
        addressColumn: columns.addressHeader ?? null,
        cityColumn: columns.cityHeader ?? null,
      },
      timeEncoding,
      periodColumns,
      globalPeriod: null,
      statusColumn: null,
      dateRangeColumns: null,
      polarity,
    };

    return { profile, confidence };
  },
};
